import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { settingsAlerts, useAlert } from 'shared/components/alerts';
import { useCompanyRelativePath } from 'ogp/hooks';
import { routes } from 'ogp/Routes';
import { UpdateCompanyThemePayload } from 'ogp/services/company-service.types';
import { useUpdateCompanyTheme } from 'ogp/hooks/queryHooks/companies/use-update-company-theme';
import { useThemeContext } from 'shared/contexts/theme-provider';
import type {
  UseSettingsEditThemeHandlersParams,
  UseSettingsEditThemeHandlersResult,
} from './use-settings-edit-theme-handlers.types';
import type { SettingEditThemeFormData } from '../setting-edit-theme/setting-edit-theme-form.types';

const useSettingsEditThemeHandlers = ({
  formMethods,
  originalThemeRef,
}: UseSettingsEditThemeHandlersParams): UseSettingsEditThemeHandlersResult => {
  const { setTheme } = useThemeContext();
  const { alertSuccess, alertError } = useAlert();
  const { t } = useTranslation('ogp-screen-specific');
  const mutation = useUpdateCompanyTheme();
  const generatePath = useCompanyRelativePath();
  const navigate = useNavigate();

  const handleSubmit = () => {
    formMethods.handleSubmit((data: SettingEditThemeFormData) => {
      const wideFile = data.logo?.portalLogoWide instanceof File ? data.logo.portalLogoWide : null;
      const squareFile =
        data.logo?.portalLogoSquare instanceof File ? data.logo.portalLogoSquare : null;

      const hasExistingLogos =
        !!originalThemeRef.current.logo?.portalLogoWideUrl &&
        !!originalThemeRef.current.logo?.portalLogoSquareUrl;
      const isAddingNewLogos = (wideFile || squareFile) && !hasExistingLogos;
      if (isAddingNewLogos && (!wideFile || !squareFile)) {
        alertError(
          new Error(t('settings-theme.logos-both-required', { ns: 'all-platforms-common' }))
        );
        return;
      }

      const payload: UpdateCompanyThemePayload = {
        colors: {
          primaryColor: data.colors?.primaryColor ?? '',
          secondaryColor: data.colors?.secondaryColor ?? '',
          primaryAccent: data.colors?.primaryAccent ?? '',
          secondaryAccent: data.colors?.secondaryAccent ?? '',
        },
      };
      if (wideFile || squareFile) {
        const logo: UpdateCompanyThemePayload['logo'] = {};
        if (wideFile) {
          logo.portalLogoWide = wideFile;
        }
        if (squareFile) {
          logo.portalLogoSquare = squareFile;
        }
        payload.logo = logo;
      }

      return mutation.mutate(payload, {
        onSuccess: () => {
          alertSuccess(t(settingsAlerts.success.updated));
          navigate(generatePath(routes.Settings));
        },
        onError: (e) => alertError(e),
      });
    })();
  };

  const handleCancel = () => {
    setTheme(originalThemeRef.current);
    formMethods.reset({
      colors: {
        primaryColor: originalThemeRef.current.primaryColor,
        secondaryColor: originalThemeRef.current.secondaryColor,
        primaryAccent: originalThemeRef.current.primaryAccent,
        secondaryAccent: originalThemeRef.current.secondaryAccent,
      },
      logo: {
        portalLogoWide: originalThemeRef.current.logo?.portalLogoWideUrl ?? null,
        portalLogoSquare: originalThemeRef.current.logo?.portalLogoSquareUrl ?? null,
      },
    });
    navigate(generatePath(routes.Settings));
  };

  return { handleSubmit, handleCancel };
};

export { useSettingsEditThemeHandlers };
